import type { MouseEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import {
    endsTextSelection,
    opensOutsideTheApp,
    SELECTABLE_LINK_SX
} from '../../components/routerLink';

/** One Teil of the Planum/Profil currently open, as the bar lists it. */
export interface DescriptionPartEntry {
    id: string;
    /** The Teil mark; empty only in the broken state the form then reports. */
    partLabel: string;
    /** Route to that Teil's own editor. */
    to: string;
}

/**
 * The Teile of one Planum/Profil, as a row of chips above the form.
 *
 * A Teil is a description RECORD of its own, so this bar NAVIGATES; it is not a
 * tab strip over one form's state. Each chip is a real link, and the click goes
 * through the caller's guarded navigate so unsaved edits get their prompt.
 *
 * "Teil hinzufügen" only opens once THIS description carries a mark: a Planum is
 * either one undivided description or a set of Teile, never both (see
 * describedSlotIssue).
 */
export default function DescriptionPartBar({
    parts,
    currentId,
    currentLabel,
    onOpen,
    onAdd
}: {
    /** Every description of this Befund + number, this one included. */
    parts: readonly DescriptionPartEntry[];
    /** The record the form has open, or null while it is unsaved. */
    currentId: string | null;
    /** The mark as currently TYPED — the open chip follows the input live. */
    currentLabel: string;
    onOpen: (to: string) => void;
    /** Omitted → the record cannot seed a sibling (unsaved, or read-only). */
    onAdd?: () => void;
}) {
    const intl = useIntl();
    const marked = currentLabel.trim() !== '';
    const canAdd = Boolean(onAdd) && marked && currentId !== null;
    // An UNSAVED description is not among `parts`, so there a single sibling
    // already means this number is split — and which marks are taken is what
    // the recorder needs to see while typing their own.
    const showChips =
        parts.length > 1 || marked || (currentId === null && parts.length > 0);
    if (!showChips && !onAdd) {
        return null;
    }
    const chipFor = (entry: DescriptionPartEntry) => {
        const isCurrent = entry.id === currentId;
        // The open Teil reads what the input says, not what is stored — renaming
        // it must not leave the chip claiming the old mark.
        const label = isCurrent ? currentLabel.trim() : entry.partLabel.trim();
        const text = label
            ? intl.formatMessage({ id: 'description.parts.one' }, { label })
            : intl.formatMessage({ id: 'description.parts.none' });
        if (isCurrent) {
            return (
                <Chip
                    key={entry.id}
                    size="small"
                    color="primary"
                    label={text}
                />
            );
        }
        const handleClick = (event: MouseEvent) => {
            if (opensOutsideTheApp(event)) {
                return;
            }
            event.preventDefault();
            if (endsTextSelection(event)) {
                return;
            }
            onOpen(entry.to);
        };
        return (
            <Chip
                key={entry.id}
                size="small"
                variant="outlined"
                label={text}
                component={RouterLink}
                to={entry.to}
                draggable={false}
                sx={SELECTABLE_LINK_SX}
                clickable
                onClick={handleClick}
            />
        );
    };
    return (
        <Box>
            <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{ flexWrap: 'wrap', alignItems: 'center' }}
            >
                <Typography variant="body2" color="text.secondary">
                    <FormattedMessage id="description.parts.title" />
                </Typography>
                {showChips && parts.map(chipFor)}
                <Tooltip
                    title={
                        canAdd
                            ? ''
                            : intl.formatMessage({
                                  id: 'description.parts.addHint'
                              })
                    }
                >
                    {/* A disabled Chip swallows pointer events, so the tooltip
                        needs a wrapper that still receives them. */}
                    <Box component="span">
                        <Chip
                            size="small"
                            variant="outlined"
                            icon={<AddIcon />}
                            label={
                                <FormattedMessage id="description.parts.add" />
                            }
                            clickable={canAdd}
                            disabled={!canAdd}
                            onClick={canAdd ? onAdd : undefined}
                        />
                    </Box>
                </Tooltip>
            </Stack>
        </Box>
    );
}
